<template>
  <div class="notification-center">
    <!-- Badge de notification (toujours visible) -->
    <button
      @click="togglePanel"
      class="notification-badge fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-2xl shadow-blue-600/40 transition-all duration-200 hover:scale-110 border-2 border-blue-400/30 flex items-center justify-center cursor-pointer group"
      :class="{ 'animate-bounce': unreadCount > 0 }"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>

      <!-- Compteur de notifications non lues -->
      <span
        v-if="unreadCount > 0"
        class="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-black rounded-full flex items-center justify-center border-2 border-slate-900 shadow-lg animate-pulse"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Panel de notifications (déroulant depuis le bas) -->
    <Transition name="slide-up">
      <div
        v-if="isPanelOpen"
        class="notification-panel fixed bottom-24 right-6 z-50 w-96 max-h-[500px] bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden flex flex-col"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50">
          <div>
            <h3 class="text-sm font-black text-white">
              {{ t.notifications }}
            </h3>
            <p class="text-xs text-slate-400 mt-0.5">
              {{ notifications.length }} {{ notifications.length > 1 ? t.total : t.totalSingular }}
            </p>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="unreadCount > 0"
              @click="markAllAsRead"
              class="px-2 py-1 text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors"
            >
              {{ t.markAllRead }}
            </button>
            <button
              @click="clearAll"
              class="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              :title="t.clearAll"
            >
              <svg class="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Liste des notifications -->
        <div class="flex-1 overflow-y-auto scrollbar-thin p-2">
          <div v-if="notifications.length === 0" class="text-center py-12 text-slate-500">
            <svg class="w-12 h-12 mx-auto mb-3 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p class="text-sm font-medium">{{ t.noNotifications }}</p>
          </div>

          <TransitionGroup name="list">
            <div
              v-for="notif in sortedNotifications"
              :key="notif.id"
              class="notification-item p-3 mb-2 rounded-xl transition-all duration-200 cursor-pointer"
              :class="[
                notif.read ? 'bg-slate-800/40' : 'bg-blue-900/20 border border-blue-700/30',
                getNotificationTypeClass(notif.type)
              ]"
              @click="markAsRead(notif.id)"
            >
              <div class="flex items-start gap-3">
                <!-- Icône -->
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-xl"
                  :class="getIconBgClass(notif.type)"
                >
                  {{ notif.icon }}
                </div>

                <!-- Contenu -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-start justify-between mb-1">
                    <p class="text-xs font-bold text-white leading-tight">
                      {{ notif.title }}
                    </p>
                    <span v-if="!notif.read" class="w-2 h-2 bg-blue-500 rounded-full shrink-0 ml-2"></span>
                  </div>
                  <p class="text-xs text-slate-400 leading-snug">
                    {{ notif.message }}
                  </p>
                  <div class="flex items-center gap-2 mt-2">
                    <span class="text-[10px] text-slate-500 font-medium">
                      {{ formatTime(notif.timestamp) }}
                    </span>
                    <span
                      v-if="notif.count && notif.count > 1"
                      class="px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded text-[10px] font-bold"
                    >
                      ×{{ notif.count }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </div>
      </div>
    </Transition>

    <!-- Overlay de fond (optionnel) -->
    <Transition name="fade">
      <div
        v-if="isPanelOpen"
        @click="togglePanel"
        class="fixed inset-0 bg-black/20 z-40"
      ></div>
    </Transition>
  </div>
</template>

<script>
export default {
  name: "NotificationCenter",

  data() {
    return {
      isPanelOpen: false,
      notifications: [],
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      translations: {
        fr: {
          notifications: "Notifications",
          total: "au total",
          totalSingular: "au total",
          markAllRead: "Tout marquer lu",
          clearAll: "Tout effacer",
          noNotifications: "Aucune notification",
          justNow: "À l'instant",
          minutesAgo: "min",
          hoursAgo: "h"
        },
        en: {
          notifications: "Notifications",
          total: "total",
          totalSingular: "total",
          markAllRead: "Mark all read",
          clearAll: "Clear all",
          noNotifications: "No notifications",
          justNow: "Just now",
          minutesAgo: "min ago",
          hoursAgo: "h ago"
        },
        de: {
          notifications: "Benachrichtigungen",
          total: "gesamt",
          totalSingular: "gesamt",
          markAllRead: "Alle als gelesen",
          clearAll: "Alle löschen",
          noNotifications: "Keine Benachrichtigungen",
          justNow: "Gerade eben",
          minutesAgo: "min",
          hoursAgo: "Std"
        },
        it: {
          notifications: "Notifiche",
          total: "totale",
          totalSingular: "totale",
          markAllRead: "Segna tutte come lette",
          clearAll: "Cancella tutto",
          noNotifications: "Nessuna notifica",
          justNow: "Proprio ora",
          minutesAgo: "min fa",
          hoursAgo: "h fa"
        }
      }
    };
  },

  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    },

    unreadCount() {
      return this.notifications.filter(n => !n.read).length;
    },

    sortedNotifications() {
      return [...this.notifications].sort((a, b) => b.timestamp - a.timestamp);
    }
  },

  methods: {
    togglePanel() {
      this.isPanelOpen = !this.isPanelOpen;
    },

    /**
     * Ajoute une notification au centre
     * @param {Object} notif - { icon, title, message, type, playSound }
     */
    addNotification(notif) {
      const newNotif = {
        id: Date.now() + Math.random(),
        icon: notif.icon || "🔔",
        title: notif.title,
        message: notif.message,
        type: notif.type || "info", // info, success, warning, error
        timestamp: Date.now(),
        read: false,
        count: 1
      };

      // Vérifier si une notification similaire existe déjà (groupement)
      const settings = JSON.parse(localStorage.getItem("notificationSettings") || "{}");
      if (settings.groupSimilar !== false) {
        const similar = this.notifications.find(
          n => !n.read &&
          n.title === newNotif.title &&
          (Date.now() - n.timestamp) < (settings.groupDelay || 3000)
        );

        if (similar) {
          similar.count++;
          similar.timestamp = Date.now();
          return;
        }
      }

      this.notifications.unshift(newNotif);

      // Limiter à 50 notifications
      if (this.notifications.length > 50) {
        this.notifications = this.notifications.slice(0, 50);
      }

      // Jouer un son si activé
      if (notif.playSound !== false && settings.playSound !== false) {
        this.playNotificationSound(settings.soundVolume || 0.5);
      }

      // Sauvegarder dans localStorage
      this.saveNotifications();
    },

    markAsRead(id) {
      const notif = this.notifications.find(n => n.id === id);
      if (notif) {
        notif.read = true;
        this.saveNotifications();
      }
    },

    markAllAsRead() {
      this.notifications.forEach(n => n.read = true);
      this.saveNotifications();
    },

    clearAll() {
      this.notifications = [];
      this.saveNotifications();
    },

    formatTime(timestamp) {
      const diff = Date.now() - timestamp;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);

      if (minutes < 1) return this.t.justNow;
      if (minutes < 60) return `${minutes} ${this.t.minutesAgo}`;
      if (hours < 24) return `${hours} ${this.t.hoursAgo}`;
      return new Date(timestamp).toLocaleDateString();
    },

    getNotificationTypeClass() {
      // Classes déjà appliquées dans le template
      return "";
    },

    getIconBgClass(type) {
      const classes = {
        success: "bg-emerald-500/20 text-emerald-400",
        error: "bg-red-500/20 text-red-400",
        warning: "bg-orange-500/20 text-orange-400",
        info: "bg-blue-500/20 text-blue-400"
      };
      return classes[type] || classes.info;
    },

    playNotificationSound(volume = 0.5) {
      try {
        // Créer un son simple (beep)
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800;
        oscillator.type = "sine";
        gainNode.gain.value = volume;

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (err) {
        console.error("❌ Erreur son notification:", err);
      }
    },

    saveNotifications() {
      try {
        const toSave = this.notifications.slice(0, 20); // Garder seulement les 20 dernières
        localStorage.setItem("notifications", JSON.stringify(toSave));
      } catch (err) {
        console.error("❌ Erreur sauvegarde notifications:", err);
      }
    },

    loadNotifications() {
      try {
        const saved = localStorage.getItem("notifications");
        if (saved) {
          this.notifications = JSON.parse(saved);
        }
      } catch (err) {
        console.error("❌ Erreur chargement notifications:", err);
      }
    }
  },

  mounted() {
    this.loadNotifications();

    // Exposer la méthode addNotification globalement
    window.$notificationCenter = this;
  }
};
</script>

<style scoped>
/* Scrollbar fine */
.scrollbar-thin::-webkit-scrollbar {
  width: 4px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 10px;
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s;
}
.list-enter-from {
  opacity: 0;
  transform: translateX(-20px);
}
.list-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.list-move {
  transition: transform 0.3s;
}
</style>
